import React from 'react';
import { motion } from 'motion/react';
import SEO from '../components/SEO';

import {
  Lightbulb,
  Lightning,
  Target,
  Trophy,
  UsersThree
} from '@phosphor-icons/react';
const About = () => {
  const springConfig = {
    type: 'spring',
    damping: 80,
    stiffness: 400,
    mass: 1,
  };

  const dna = [
    {
      icon: UsersThree,
      title: 'Built for Business',
      description: 'Every feature exists to help you capture more jobs, save time, and grow. No experiments. No vanity features.',
    },
    {
      icon: Lightbulb,
      title: 'Powered by AI',
      description: 'Not AI for the sake of it. AI that responds to leads, follows up on quotes, and handles the admin you never get to.',
    },
    {
      icon: Target,
      title: 'Obsessed with Results',
      description: 'We measure success in jobs booked, leads captured, and hours saved. Your results are our scoreboard.',
    },
  ];

  return (
    <div className="pt-16 bg-[#161B26]">
      <SEO 
        title="About Man & Machine - AI-Powered Business Intelligence SaaS"
        description="Man & Machine is a SaaS-first platform that puts business growth at the center. We blend AI automation with real-world strategy to help businesses scale smarter, faster, and with zero BS."
        keywords="AI SaaS platform, business intelligence software, AI automation tools, business growth platform, AI-powered business tools"
        canonicalUrl="https://manmachine.agency/about"
      />
      {/* Hero Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/3 w-96 h-96 bg-[#6366F1] rounded-full filter blur-3xl opacity-20"></div>
          <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-[#EC4899] rounded-full filter blur-3xl opacity-20"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="display-hero text-white mb-8">
              AI systems for service businesses. <span className="text-[#6366F1]">Respond instantly. Book more jobs. Stop chasing admin.</span>
            </h1>
            <p className="body-large text-[#A0AEC0] max-w-3xl mx-auto">
              Man & Machine helps service businesses capture more leads, follow up on time, and take hours of admin off your plate. We find where you're losing time and revenue, then set up systems that fix it automatically.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Belief Section */}
      <section className="py-20 bg-[#12141A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-6">
                <Lightning size={16} className="text-[#14F195]" />
                <span className="text-sm text-[#A0AEC0] font-medium">Our Philosophy</span>
              </div>
              <h2 className="heading-large text-white mb-6">
                Software should solve real problems. AI should save you real time and make you real money.
              </h2>
              <p className="body-large text-[#A0AEC0] mb-4">
                Too much "AI" today is theoretical, complicated, or disconnected from what service businesses actually need.
              </p>
              <p className="body-large text-[#A0AEC0] mb-4">
                We believe AI should:
              </p>
              <ul className="body-large text-[#A0AEC0] mb-6 space-y-2 ml-6">
                <li className="flex items-start gap-2">
                  <span className="text-[#14F195] mt-1">•</span>
                  <span>Respond to leads before they go cold</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#14F195] mt-1">•</span>
                  <span>Follow up on quotes without you chasing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#14F195] mt-1">•</span>
                  <span>Handle the admin that eats into your billable hours</span>
                </li>
              </ul>
              <p className="body-large text-[#A0AEC0] mb-6">
                That's why Man & Machine is built for real businesses, focused on real outcomes, and simple enough to set up in minutes.
              </p>
              <p className="body-large text-white font-semibold">
                Business-first. Results-obsessed. Zero fluff.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="rounded-2xl overflow-hidden card-glow"
            >
              <img
                src="https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a"
                alt="Team Collaboration"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our DNA Section */}
      <section className="py-20 bg-[#161B26]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <h2 className="heading-large text-white mb-4">Our DNA</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {dna.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                  className="glass rounded-2xl p-8 hover:bg-[rgba(255,255,255,0.08)] transition-all hover-lift card-glow"
                >
                  <div className="inline-flex p-4 rounded-xl bg-[rgba(99,102,241,0.1)] mb-4">
                    <Icon size={32} className="text-[#6366F1]" />
                  </div>
                  <h3 className="heading-medium text-white mb-3">{item.title}</h3>
                  <p className="body-standard text-[#A0AEC0]">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Man & Machine Section */}
      <section className="py-20 bg-[#12141A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="rounded-2xl overflow-hidden order-2 md:order-1 card-glow"
            >
              <img
                src="https://images.unsplash.com/photo-1680459575585-390ed5cfcae0"
                alt="Strategic Planning"
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="order-1 md:order-2"
            >
              <div className="inline-flex p-3 rounded-xl bg-[rgba(99,102,241,0.1)] mb-6">
                <Trophy size={32} className="text-[#6366F1]" />
              </div>
              <h2 className="heading-large text-white mb-6">Why <span className="text-[#6366F1]">Man & Machine</span>?</h2>
              <p className="body-large text-[#A0AEC0] mb-4">
                Because you're not here for another tool you won't use or a consulting report that sits in a drawer.
              </p>
              <p className="body-large text-[#A0AEC0] mb-4">
                You're here to:
              </p>
              <ul className="body-large text-[#A0AEC0] mb-6 space-y-2 ml-6">
                <li className="flex items-start gap-2">
                  <span className="text-[#14F195] mt-1">•</span>
                  <span>Stop missing leads and enquiries</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#14F195] mt-1">•</span>
                  <span>Book more jobs without more staff</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#14F195] mt-1">•</span>
                  <span>Get hours of admin off your plate</span>
                </li>
              </ul>
              <p className="body-large text-[#A0AEC0]">
                Man & Machine combines your business knowledge with smart automation, so you can focus on the work that pays while the platform handles the rest.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;